<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, useVueFlow, Handle, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import FlowsAPI from 'dashboard/api/flows';
import BroadcastsAPI from 'dashboard/api/broadcasts';
import Button from 'dashboard/components-next/button/Button.vue';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const route = useRoute();
const router = useRouter();
const store = useStore();
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');

const flowId = route.params.flowId;
const accountId = () => store.getters.getCurrentAccountId;

// dicas (constantes p/ nao quebrar o parser do template com chaves)
const nomeHint = '{{nome}}';
const spinHint = '{a|b}';

const {
  addNodes,
  addEdges,
  removeNodes,
  onConnect,
  onNodeClick,
  findNode,
  setNodes,
  setEdges,
  getNodes,
  getEdges,
} = useVueFlow('flow-editor');

const loaded = ref(false);
const saving = ref(false);
const name = ref('Novo flow');
const status = ref('draft');
const keywords = ref('');
const catchAll = ref(false);
// caixas onde o flow atua (vazio = todas)
const inboxIds = ref([]);
const selectedId = ref(null);

const PALETTE = [
  { type: 'message', label: 'Mensagem', icon: '💬' },
  { type: 'wait', label: 'Espera', icon: '⏱️' },
  { type: 'question', label: 'Pergunta', icon: '❓' },
  { type: 'label', label: 'Etiqueta', icon: '🏷️' },
  { type: 'end', label: 'Fim', icon: '🏁' },
];

const NODE_META = {
  start: { icon: '▶️', label: 'Início' },
  message: { icon: '💬', label: 'Mensagem' },
  wait: { icon: '⏱️', label: 'Espera' },
  question: { icon: '❓', label: 'Pergunta' },
  label: { icon: '🏷️', label: 'Etiqueta' },
  end: { icon: '🏁', label: 'Fim' },
};

let counter = 0;
const genId = () => `n_${Date.now()}_${counter++}`;
// id ESTAVEL do ramo — a aresta usa esse id, entao renomear o valor nao quebra.
const bid = () => `b_${Date.now()}_${counter++}`;

const defaultData = type => {
  switch (type) {
    case 'message':
      return { content: '' };
    case 'wait':
      return { seconds: 30 };
    case 'question':
      return {
        content: '',
        variable: '',
        branches: [
          { id: bid(), value: 'Sim' },
          { id: bid(), value: 'Não' },
        ],
      };
    case 'label':
      return { label_ids: [] };
    default:
      return {};
  }
};

const addNode = type => {
  const id = genId();
  const n = counter;
  // espalha os novos nos numa grade pra nao empilharem em cima uns dos outros
  addNodes([
    {
      id,
      type,
      position: { x: 220 + (n % 4) * 240, y: 160 + Math.floor(n / 4) * 140 },
      data: defaultData(type),
    },
  ]);
  selectedId.value = id;
};

const selected = computed(() =>
  selectedId.value ? findNode(selectedId.value) : null
);

const removeSelected = () => {
  if (!selectedId.value || selectedId.value === 'start') return;
  removeNodes([selectedId.value]);
  selectedId.value = null;
};

// midia opcional do no de mensagem (upload -> blob_id; o motor envia como anexo)
const nodeMediaUploading = ref(false);
const handleNodeMedia = async event => {
  const file = event.target.files[0];
  if (!file || !selected.value) return;
  nodeMediaUploading.value = true;
  try {
    const { data } = await BroadcastsAPI.uploadMedia(file);
    selected.value.data.attachment_blob_id = data.blob_id;
    selected.value.data.attachment_url = data.file_url;
    selected.value.data.attachment_name = file.name;
    useAlert('Mídia carregada');
  } catch (e) {
    useAlert('Erro ao carregar a mídia');
  } finally {
    nodeMediaUploading.value = false;
  }
};
const removeNodeMedia = () => {
  if (!selected.value) return;
  delete selected.value.data.attachment_blob_id;
  delete selected.value.data.attachment_url;
  delete selected.value.data.attachment_name;
};
const isImageFile = name => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');

const addBranch = () => {
  if (!selected.value) return;
  selected.value.data.branches = [
    ...(selected.value.data.branches || []),
    { id: bid(), value: '' },
  ];
};
const removeBranch = i => {
  selected.value.data.branches.splice(i, 1);
};

onConnect(params => addEdges([params]));
onNodeClick(({ node }) => {
  selectedId.value = node.id;
});

const load = async () => {
  try {
    const { data } = await FlowsAPI.show(flowId);
    name.value = data.name;
    status.value = data.status;
    const cfg = data.trigger_config || {};
    keywords.value = (cfg.keywords || []).join(', ');
    catchAll.value = cfg.catch_all === true;
    inboxIds.value = (cfg.inbox_ids || []).map(Number);
    setNodes(data.nodes || []);
    setEdges(data.edges || []);
  } catch (e) {
    useAlert('Erro ao carregar o flow');
  } finally {
    loaded.value = true;
  }
};

const buildPayload = extra => ({
  flow: {
    name: name.value,
    nodes: getNodes.value.map(n => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data,
    })),
    edges: getEdges.value.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
    })),
    trigger_config: {
      type: 'message',
      keywords: keywords.value
        .split(',')
        .map(k => k.trim())
        .filter(Boolean),
      catch_all: catchAll.value,
      inbox_ids: inboxIds.value.map(Number),
    },
    ...extra,
  },
});

const save = async (newStatus = null) => {
  saving.value = true;
  try {
    const extra = newStatus ? { status: newStatus } : {};
    const { data } = await FlowsAPI.update(flowId, buildPayload(extra));
    status.value = data.status;
    useAlert('Flow salvo');
  } catch (e) {
    useAlert('Erro ao salvar');
  } finally {
    saving.value = false;
  }
};

const toggleActive = () => save(status.value === 'active' ? 'draft' : 'active');
// o editor anterior e aberto pela lista de Flows (kanban_flows), que mostra os flows anteriores
const goBack = () =>
  router.push({ name: 'kanban_flows', params: { accountId: accountId() } });

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  load();
});
</script>

<template>
  <div class="flex flex-col h-full w-full overflow-hidden bg-n-background">
    <!-- Barra superior -->
    <div
      class="flex items-center gap-3 px-4 h-16 border-b border-n-slate-3 bg-n-solid-1 flex-wrap"
    >
      <Button
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        title="Voltar"
        @click="goBack"
      />
      <input
        v-model="name"
        class="h-9 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-transparent hover:outline-n-weak focus:outline-n-blue-9 bg-transparent text-n-slate-12 font-semibold text-base transition-all"
      />
      <span
        class="px-2 py-0.5 text-xs font-medium rounded-md"
        :class="
          status === 'active'
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-slate-3 text-n-slate-11'
        "
      >
        {{ status === 'active' ? 'Ativo' : 'Rascunho' }}
      </span>
      <div class="flex items-center gap-2 ml-auto">
        <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
          Palavras-chave
          <input
            v-model="keywords"
            placeholder="ex: menu, oi"
            class="h-9 w-44 px-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all"
          />
        </label>
        <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
          <input v-model="catchAll" type="checkbox" /> qualquer msg
        </label>
        <Button
          label="Salvar"
          icon="i-lucide-save"
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="saving"
          @click="save()"
        />
        <Button
          :label="status === 'active' ? 'Pausar' : 'Ativar'"
          :icon="status === 'active' ? 'i-lucide-pause' : 'i-lucide-play'"
          :color="status === 'active' ? 'amber' : 'blue'"
          size="sm"
          :is-loading="saving"
          @click="toggleActive"
        />
      </div>
    </div>

    <!-- Caixas onde o flow atua -->
    <div
      class="flex items-center gap-2 px-4 py-2 border-b border-n-slate-3 bg-n-solid-1 text-xs flex-wrap"
    >
      <span class="text-n-slate-11 font-medium">Ativo nas caixas:</span>
      <label
        v-for="i in inboxes"
        :key="i.id"
        class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-n-slate-11 cursor-pointer has-[:checked]:bg-n-blue-3 has-[:checked]:outline-n-blue-9 has-[:checked]:text-n-blue-9 dark:has-[:checked]:text-n-blue-11"
      >
        <input
          v-model="inboxIds"
          type="checkbox"
          :value="i.id"
          class="accent-n-blue-9"
        />
        {{ i.name }}
      </label>
      <template v-if="!inboxIds.length">
        <span class="text-n-slate-10">nenhuma marcada = TODAS as caixas</span>
      </template>
    </div>

    <div class="flex flex-1 min-h-0">
      <!-- Paleta -->
      <div
        class="w-44 shrink-0 border-r border-n-slate-3 bg-n-solid-1 p-3 flex flex-col gap-2"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-[0.04em] text-n-slate-9"
        >
          Adicionar nó
        </span>
        <button
          v-for="p in PALETTE"
          :key="p.type"
          class="flex items-center gap-2 h-9 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
          @click="addNode(p.type)"
        >
          <span>{{ p.icon }}</span> {{ p.label }}
        </button>
      </div>

      <!-- Canvas -->
      <div class="flex-1 min-h-0 relative">
        <VueFlow
          v-if="loaded"
          id="flow-editor"
          class="h-full w-full"
          fit-view-on-init
          :min-zoom="0.2"
          :max-zoom="2"
        >
          <Background pattern-color="#aaa" :gap="16" />
          <Controls />

          <template #node-start="{ data }">
            <div
              class="px-3 py-2 rounded-lg bg-n-blue-9 text-white text-xs shadow"
            >
              ▶️ {{ data.label || 'Início' }}
              <Handle type="source" :position="Position.Bottom" />
            </div>
          </template>

          <template #node-message="{ data }">
            <div
              class="px-3 py-2 rounded-lg bg-white dark:bg-n-solid-2 outline outline-1 outline-n-container text-xs min-w-[140px] shadow"
            >
              <Handle type="target" :position="Position.Top" />
              <div class="font-medium text-n-slate-12">💬 Mensagem</div>
              <img
                v-if="data.attachment_url && isImageFile(data.attachment_name)"
                :src="data.attachment_url"
                class="rounded my-1 max-h-16 w-full object-cover"
              />
              <div
                v-else-if="data.attachment_name"
                class="text-n-slate-11 truncate max-w-[160px]"
              >
                📎 {{ data.attachment_name }}
              </div>
              <div class="text-n-slate-11 truncate max-w-[160px]">
                {{ data.content || '(vazio)' }}
              </div>
              <Handle type="source" :position="Position.Bottom" />
            </div>
          </template>

          <template #node-wait="{ data }">
            <div
              class="px-3 py-2 rounded-lg bg-white dark:bg-n-solid-2 outline outline-1 outline-n-container text-xs shadow"
            >
              <Handle type="target" :position="Position.Top" />
              <div class="font-medium text-n-slate-12">
                ⏱️ Espera {{ data.seconds }}s
              </div>
              <Handle type="source" :position="Position.Bottom" />
            </div>
          </template>

          <template #node-question="{ data }">
            <div
              class="px-3 py-2 rounded-lg bg-white dark:bg-n-solid-2 outline outline-1 outline-n-container text-xs min-w-[160px] shadow relative"
            >
              <Handle type="target" :position="Position.Top" />
              <div class="font-medium text-n-slate-12">❓ Pergunta</div>
              <div class="text-n-slate-11 truncate max-w-[180px]">
                {{ data.content || '(vazio)' }}
              </div>
              <div class="mt-1 flex flex-col gap-1">
                <div
                  v-for="(b, i) in data.branches"
                  :key="b.id || i"
                  class="relative text-right pr-2 text-n-slate-11"
                >
                  {{ b.value || '?' }}
                  <Handle
                    :id="b.id || b.value"
                    type="source"
                    :position="Position.Right"
                    :style="{ top: `${40 + i * 20}px` }"
                  />
                </div>
              </div>
              <div class="text-right pr-2 text-n-slate-10">senão</div>
              <Handle id="fallback" type="source" :position="Position.Bottom" />
            </div>
          </template>

          <template #node-label="{ data }">
            <div
              class="px-3 py-2 rounded-lg bg-white dark:bg-n-solid-2 outline outline-1 outline-n-container text-xs shadow"
            >
              <Handle type="target" :position="Position.Top" />
              <div class="font-medium text-n-slate-12">
                🏷️ Etiqueta ({{ (data.label_ids || []).length }})
              </div>
              <Handle type="source" :position="Position.Bottom" />
            </div>
          </template>

          <template #node-end="{ data }">
            <div
              class="px-3 py-2 rounded-lg bg-n-slate-12 text-white text-xs shadow"
            >
              <Handle type="target" :position="Position.Top" />
              🏁 {{ data.label || 'Fim' }}
            </div>
          </template>
        </VueFlow>
      </div>

      <!-- Painel de propriedades -->
      <div
        v-if="selected"
        class="w-80 shrink-0 border-l border-n-slate-3 bg-n-solid-1 p-4 flex flex-col gap-3 overflow-auto"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ NODE_META[selected.type]?.icon }}
            {{ NODE_META[selected.type]?.label }}
          </span>
          <button
            v-if="selected.id !== 'start'"
            class="text-n-ruby-11 text-xs hover:underline"
            @click="removeSelected"
          >
            Remover nó
          </button>
        </div>

        <template v-if="selected.type === 'message'">
          <label class="text-xs text-n-slate-11"
            >Texto (aceita {{ nomeHint }} e {{ spinHint }})</label
          >
          <textarea
            v-model="selected.data.content"
            rows="4"
            class="p-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all"
          />
          <label class="text-xs text-n-slate-11"
            >Mídia (opcional — imagem/áudio/vídeo/documento)</label
          >
          <input
            type="file"
            accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
            class="text-xs"
            @change="handleNodeMedia"
          />
          <template v-if="nodeMediaUploading">
            <span class="text-xs text-n-slate-11">Carregando...</span>
          </template>
          <div
            v-else-if="selected.data.attachment_name"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-n-teal-11 truncate"
              >✓ {{ selected.data.attachment_name }}</span
            >
            <button
              class="text-n-ruby-11 text-xs hover:underline"
              @click="removeNodeMedia"
            >
              remover
            </button>
          </div>
        </template>

        <template v-else-if="selected.type === 'wait'">
          <label class="text-xs text-n-slate-11">Segundos de espera</label>
          <input
            v-model.number="selected.data.seconds"
            type="number"
            min="0"
            class="h-9 px-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all"
          />
        </template>

        <template v-else-if="selected.type === 'question'">
          <label class="text-xs font-medium text-n-slate-12"
            >Texto da pergunta (enviado ao contato)</label
          >
          <textarea
            v-model="selected.data.content"
            rows="3"
            placeholder="Ex: Você quer falar com Vendas ou Suporte?"
            class="p-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all"
          />
          <label class="text-xs text-n-slate-11"
            >Guardar a resposta na variável (opcional)</label
          >
          <input
            v-model="selected.data.variable"
            placeholder="ex: resposta"
            class="h-9 px-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all"
          />
          <div class="flex items-center justify-between">
            <span class="text-xs text-n-slate-11"
              >Respostas esperadas (ramos)</span
            >
            <button
              class="text-n-blue-9 dark:text-n-blue-11 text-xs hover:underline"
              @click="addBranch"
            >
              + ramo
            </button>
          </div>
          <div
            v-for="(b, i) in selected.data.branches"
            :key="b.id || i"
            class="flex items-center gap-2"
          >
            <input
              v-model="b.value"
              placeholder="ex: Sim"
              class="h-8 flex-1 px-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all text-sm"
            />
            <button
              class="text-n-ruby-11 text-xs hover:underline"
              @click="removeBranch(i)"
            >
              x
            </button>
          </div>
          <p class="text-[11px] text-n-slate-10">
            Arraste da bolinha de cada resposta (à direita do nó) até o próximo
            passo. A bolinha "senão" (embaixo) é o caminho padrão quando nada
            casa.
          </p>
        </template>

        <template v-else-if="selected.type === 'label'">
          <label class="text-xs text-n-slate-11">Etiquetas a aplicar</label>
          <div class="flex flex-col gap-1 max-h-60 overflow-auto">
            <label
              v-for="l in labels"
              :key="l.id"
              class="flex items-center gap-1 text-sm text-n-slate-11"
            >
              <input
                v-model="selected.data.label_ids"
                type="checkbox"
                :value="l.id"
              />
              {{ l.title }}
            </label>
          </div>
        </template>

        <template v-else>
          <p class="text-xs text-n-slate-11">Este nó não tem configurações.</p>
        </template>
      </div>
    </div>
  </div>
</template>
